import { env } from '@/lib/env';
import { getAuthItem, removeAuthItem, AUTH_STORAGE_KEYS } from '@/lib/authStorage';
import { getActiveWorkspaceId } from './workspace-context';
import { qaRequestId } from '@/lib/qa-telemetry';
import { refreshAccessToken } from '../apiClient';

function toHeaders(initHeaders?: HeadersInit): Headers {
  if (initHeaders instanceof Headers) return new Headers(initHeaders);
  return new Headers(initHeaders ?? {});
}

function getUserId() {
  if (typeof window !== 'undefined') {
    const userId = getAuthItem('userId');
    if (userId) return userId;

    // Fallback to guest ID from localStorage (set by SessionProvider)
    const guestId = localStorage.getItem('xavior_guest_id');
    if (guestId) return guestId;
  }
  return env.defaultUserId ?? 'anonymous';
}

export class ApiError extends Error {
  readonly status: number;
  readonly payload?: unknown;

  constructor(message: string, status: number, payload?: unknown) {
    super(message);
    this.status = status;
    this.payload = payload;
  }
}

async function toApiError(response: Response): Promise<ApiError> {
  let payload: unknown;
  try {
    const contentType = response.headers.get('Content-Type') ?? '';
    if (contentType.includes('application/json')) {
      payload = await response.json();
    } else {
      payload = await response.text();
    }
  } catch {
    payload = undefined;
  }

  // API error bodies use { error: string }; some legacy/proxy paths use { message }.
  // Surface whichever is present before falling back to a generic status message.
  const errBody = typeof payload === 'object' && payload ? (payload as any) : undefined;
  const message =
    (typeof errBody?.message === 'string' && errBody.message) ||
    (typeof errBody?.error === 'string' && errBody.error) ||
    `Request failed with status ${response.status}`;

  return new ApiError(message, response.status, payload);
}

function makeUrl(path: string): string {
  if (path.startsWith('http')) return path;
  // Normalize: ensure base has no trailing slash, path has leading slash
  const base = env.apiBaseUrl.endsWith('/') ? env.apiBaseUrl.slice(0, -1) : env.apiBaseUrl;
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${base}${normalizedPath}`;
}

function makeIdempotencyKey() {
  return crypto.randomUUID();
}

export interface ApiResponse<T> {
  data: T;
  etag?: string;
}

/**
 * Runs the request and, on a workspace-auth 401, refreshes the access token
 * and retries ONCE — parity with lib/http.ts's `request()`. `init.headers` is
 * the SAME `Headers` instance built once by the caller before the first
 * attempt, so a retried write reuses the original Idempotency-Key / If-Match
 * (only the Authorization header is swapped in place for the retry); minting
 * fresh values here would turn a retried write into a duplicate.
 *
 * EXCEPTION: an integration-level 401 (e.g. a disconnected Gmail account)
 * carries `{ connected: false }` in the body — it is not a workspace-auth
 * failure, so refreshing the workspace token can't fix it, and treating it as
 * one would clear a perfectly valid session just because an integration is
 * disconnected. Probe the body on a clone (so the caller's normal
 * `toApiError` read still works) and skip straight to the regular error path.
 */
async function fetchWithAuthRetry(
  url: string,
  init: RequestInit & { headers: Headers },
  skipRetry = false,
): Promise<Response> {
  const res = await fetch(url, init);
  if (res.ok || skipRetry || res.status !== 401) return res;

  // Only a request that actually carried a bearer token can be "refreshed".
  if (!init.headers.has('Authorization')) return res;

  const integrationAuth401 =
    ((await res
      .clone()
      .json()
      .catch(() => undefined)) as { connected?: boolean } | undefined)?.connected === false;
  if (integrationAuth401) return res;

  const newToken = await refreshAccessToken();
  if (newToken) {
    init.headers.set('Authorization', `Bearer ${newToken}`);
    return fetchWithAuthRetry(url, init, true);
  }

  // Refresh failed: this really is a dead session. Mirrors lib/http.ts's
  // unrecoverable-401 branch (#5420) — clear the token and tell the app-wide
  // SessionProvider, or it stays 'authenticated' while every request 401s.
  if (typeof window !== 'undefined') {
    removeAuthItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN);
    removeAuthItem('userEmail');
    window.dispatchEvent(new CustomEvent('xavior:session-expired'));
  }
  return res;
}

export async function apiGet<T>(url: string, init?: RequestInit): Promise<ApiResponse<T>> {
  const headers = toHeaders(init?.headers);
  headers.set('x-workspace-id', getActiveWorkspaceId());
  headers.set('x-user-id', getUserId());
  // QA-only correlation id joining this call to its API log line (no-op when off).
  const qaRid = qaRequestId();
  if (qaRid) headers.set('x-request-id', qaRid);

  const authToken = headers.get('Authorization') || (typeof window !== 'undefined' ? getAuthItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN) : null);
  if (authToken && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${authToken}`);
  }

  // Add X-User-Email header for supervisor routing
  const userEmail = typeof window !== 'undefined' ? getAuthItem('userEmail') : null;
  if (userEmail) {
    headers.set('X-User-Email', userEmail);
  }

  const res = await fetchWithAuthRetry(makeUrl(url), {
    ...init,
    method: 'GET',
    headers,
    credentials: 'include',
  });

  if (!res.ok) throw await toApiError(res);

  const etag = res.headers.get('ETag') ?? undefined;
  const data = (await res.json()) as T;
  return { data, etag };
}

export async function apiPost<T>(url: string, body: unknown, init?: RequestInit): Promise<ApiResponse<T>> {
  const headers = toHeaders(init?.headers);
  headers.set('Content-Type', 'application/json');
  // Respect caller-provided keys so callers can scope dedup per resource
  // (e.g. "create-contact-${email}") and double-click protection actually
  // works server-side. Default to a fresh UUID otherwise.
  if (!headers.has('Idempotency-Key')) headers.set('Idempotency-Key', makeIdempotencyKey());
  headers.set('x-workspace-id', getActiveWorkspaceId());
  headers.set('x-user-id', getUserId());
  // QA-only correlation id joining this call to its API log line (no-op when off).
  const qaRid = qaRequestId();
  if (qaRid) headers.set('x-request-id', qaRid);

  const authToken = headers.get('Authorization') || (typeof window !== 'undefined' ? getAuthItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN) : null);
  if (authToken && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${authToken}`);
  }

  // Add X-User-Email header for supervisor routing
  const userEmail = typeof window !== 'undefined' ? getAuthItem('userEmail') : null;
  if (userEmail) {
    headers.set('X-User-Email', userEmail);
  }

  const res = await fetchWithAuthRetry(makeUrl(url), {
    method: 'POST',
    ...init,
    headers,
    body: JSON.stringify(body),
    credentials: 'include',
  });

  if (!res.ok) throw await toApiError(res);

  const etag = res.headers.get('ETag') ?? undefined;
  const data = (await res.json()) as T;
  return { data, etag };
}

export async function apiPatch<T>(
  url: string,
  body: unknown,
  etag: string,
  init?: RequestInit,
): Promise<ApiResponse<T>> {
  const headers = toHeaders(init?.headers);
  headers.set('Content-Type', 'application/json');
  headers.set('Idempotency-Key', makeIdempotencyKey());
  headers.set('If-Match', etag);
  headers.set('x-workspace-id', getActiveWorkspaceId());
  headers.set('x-user-id', getUserId());
  // QA-only correlation id joining this call to its API log line (no-op when off).
  const qaRid = qaRequestId();
  if (qaRid) headers.set('x-request-id', qaRid);

  const authToken = headers.get('Authorization') || (typeof window !== 'undefined' ? getAuthItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN) : null);
  if (authToken && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${authToken}`);
  }

  // Add X-User-Email header for supervisor routing
  const userEmail = typeof window !== 'undefined' ? getAuthItem('userEmail') : null;
  if (userEmail) {
    headers.set('X-User-Email', userEmail);
  }

  const res = await fetchWithAuthRetry(makeUrl(url), {
    method: 'PATCH',
    ...init,
    headers,
    body: JSON.stringify(body),
    credentials: 'include',
  });

  if (!res.ok) throw await toApiError(res);

  const newEtag = res.headers.get('ETag') ?? undefined;
  const data = (await res.json()) as T;
  return { data, etag: newEtag };
}

export async function apiDelete(url: string, init?: RequestInit): Promise<void> {
  const headers = toHeaders(init?.headers);
  headers.set('x-workspace-id', getActiveWorkspaceId());
  headers.set('x-user-id', getUserId());
  // QA-only correlation id joining this call to its API log line (no-op when off).
  const qaRid = qaRequestId();
  if (qaRid) headers.set('x-request-id', qaRid);

  const authToken = headers.get('Authorization') || (typeof window !== 'undefined' ? getAuthItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN) : null);
  if (authToken && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${authToken}`);
  }

  // Add X-User-Email header for supervisor routing
  const userEmail = typeof window !== 'undefined' ? getAuthItem('userEmail') : null;
  if (userEmail) {
    headers.set('X-User-Email', userEmail);
  }

  const res = await fetchWithAuthRetry(makeUrl(url), {
    method: 'DELETE',
    ...init,
    headers,
    credentials: 'include',
  });

  if (!res.ok) throw await toApiError(res);
}
